import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText, Car, Calendar, ChevronLeft, Download, AlertTriangle,
  Loader2, Filter, Euro, Upload, Info
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { formatCurrency, formatDate } from "../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CONTRACT_STATUS_LABELS = {
  active: "Actif",
  expired: "Expiré",
  terminated: "Résilié",
};

const CONTRACT_STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  expired: "bg-red-100 text-red-800",
  terminated: "bg-slate-100 text-slate-800",
};

export default function ContractsOverview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [daysFilter, setDaysFilter] = useState("all");
  
  const token = localStorage.getItem("token");

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/fleet/contracts?page=${page}&limit=50`;
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      if (daysFilter !== "all") {
        url += `&ending_within_days=${daysFilter}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching contracts:", err);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter, daysFilter]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const getDaysRemainingBadge = (days) => {
    if (days === undefined || days === null) return null;
    if (days < 0) return <Badge className="bg-red-100 text-red-800">Expiré</Badge>;
    if (days <= 30) return <Badge className="bg-red-100 text-red-800">J-{days}</Badge>;
    if (days <= 90) return <Badge className="bg-amber-100 text-amber-800">J-{days}</Badge>;
    if (days <= 180) return <Badge className="bg-blue-100 text-blue-800">J-{days}</Badge>;
    return <Badge variant="outline">J-{days}</Badge>;
  };

  const expiringCount = contracts.filter(c => c.days_until_end >= 0 && c.days_until_end <= 90).length;
  const totalMonthly = contracts.reduce((sum, c) => sum + (c.monthly_rent_ttc || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/fleet/dashboard")}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Contrats Leasing</h1>
              <p className="text-sm text-slate-500">{total} contrat(s) • Vue transverse</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/engagements/contrats/import")}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="import-via-engagements-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer via Dépenses avec Engagements
          </Button>
        </div>
      </div>
      
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
        {/* Info : source de vérité */}
        <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3 flex items-start gap-3 text-sm">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-slate-700">
            <span className="font-medium text-blue-900">Astuce :</span> les contrats véhicule sont importés
            depuis <button type="button" onClick={() => navigate("/engagements/contrats")} className="underline text-blue-700 font-medium hover:text-blue-900">Dépenses avec Engagements</button>.
            L&apos;OCR détecte automatiquement le véhicule et crée la liaison bidirectionnelle. Cette page est une vue transverse en lecture.
          </div>
        </div>
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Total contrats</p>
              <p className="text-2xl font-bold text-slate-900">{total}</p>
            </CardContent>
          </Card>
          <Card className={expiringCount > 0 ? "border-amber-200 bg-amber-50" : ""}>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">À renouveler (90j)</p>
              <p className="text-2xl font-bold text-amber-600">{expiringCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Loyers mensuels</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalMonthly)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-slate-500">Loyers annuels</p>
              <p className="text-2xl font-bold text-indigo-600">{formatCurrency(totalMonthly * 12)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Filtres:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="expired">Expiré</SelectItem>
                  <SelectItem value="terminated">Résilié</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={daysFilter} onValueChange={setDaysFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Échéance" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes échéances</SelectItem>
                  <SelectItem value="30">30 jours</SelectItem>
                  <SelectItem value="90">90 jours</SelectItem>
                  <SelectItem value="180">180 jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Véhicule</TableHead>
                  <TableHead>N° contrat</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Bailleur</TableHead>
                  <TableHead>Fin contrat</TableHead>
                  <TableHead className="text-right">Loyer TTC</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Aucun contrat trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  contracts.map((contract) => (
                    <TableRow 
                      key={contract.id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/fleet/vehicles/${contract.vehicle_id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium">{contract.vehicle_name}</p>
                            <p className="text-xs text-slate-500 font-mono">{contract.vehicle_registration}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{contract.contract_number || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{contract.contract_type_label}</Badge>
                      </TableCell>
                      <TableCell>{contract.lessor_name || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{formatDate(contract.end_date)}</span>
                          {getDaysRemainingBadge(contract.days_until_end)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(contract.monthly_rent_ttc || 0)}
                      </TableCell>
                      <TableCell>
                        <Badge className={CONTRACT_STATUS_COLORS[contract.status] || "bg-slate-100"}>
                          {CONTRACT_STATUS_LABELS[contract.status] || contract.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
